import {
	Box,
	Button,
	Chip,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	List,
	ListItem,
	ListItemText,
	Typography,
} from "@mui/material";
import {
	getTenantExtractionConfigurations,
	getTenantUsers,
} from "Infrastructure/Api/Api";
import type {
	TenantExtractionConfigurationDto,
	TenantUserDto,
} from "Infrastructure/Api/Api";
import type { FC } from "react";
import { useEffect, useState } from "react";
import { toast } from "sonner";

type Props = {
	open: boolean;
	onClose: () => void;
	tenantId: number;
	tenantCode: string;
	type: "users" | "configurations";
};

export const TenantDetailsDialog: FC<Props> = ({
	open,
	onClose,
	tenantId,
	tenantCode,
	type,
}) => {
	const [users, setUsers] = useState<TenantUserDto[]>([]);
	const [configurations, setConfigurations] = useState<
		TenantExtractionConfigurationDto[]
	>([]);
	const [isLoading, setIsLoading] = useState(false);

	useEffect(() => {
		if (!open || !tenantId) {
			return;
		}

		// Use abort flag to prevent stale updates
		let aborted = false;

		setIsLoading(true);

		if (type === "users") {
			// Clear stale configurations data when loading users
			setConfigurations([]);

			getTenantUsers(tenantId)
				.then((response) => {
					if (aborted) return; // Don't update if component unmounted or dependencies changed
					if (response.status === 200) {
						setUsers(response.data);
					}
				})
				.catch((error) => {
					if (aborted) return;
					console.error("Failed to load tenant users:", error);
					toast.error("Failed to load users. Please try again.");
				})
				.finally(() => {
					if (!aborted) {
						setIsLoading(false);
					}
				});
		} else {
			// Clear stale users data when loading configurations
			setUsers([]);

			getTenantExtractionConfigurations(tenantId)
				.then((response) => {
					if (aborted) return; // Don't update if component unmounted or dependencies changed
					if (response.status === 200) {
						setConfigurations(response.data);
					}
				})
				.catch((error) => {
					if (aborted) return;
					console.error("Failed to load tenant configurations:", error);
					toast.error("Failed to load extraction configurations. Please try again.");
				})
				.finally(() => {
					if (!aborted) {
						setIsLoading(false);
					}
				});
		}

		// Cleanup function to abort on unmount or dependency change
		return () => {
			aborted = true;
		};
	}, [open, tenantId, type]);

	const title =
		type === "users"
			? `Users in ${tenantCode}`
			: `Extraction Configurations in ${tenantCode}`;

	return (
		<Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
			<DialogTitle>{title}</DialogTitle>
			<DialogContent>
				{isLoading ? (
					<Box
						display="flex"
						justifyContent="center"
						alignItems="center"
						minHeight={200}
					>
						<CircularProgress />
					</Box>
				) : (
					<>
						{type === "users" && (
							<>
								{users.length === 0 ? (
									<Typography color="text.secondary" textAlign="center" py={3}>
										No users found in this tenant
									</Typography>
								) : (
									<List>
										{users.map((user) => (
											<ListItem key={user.userID} divider>
												<ListItemText
													primary={user.email}
													secondary={
														<>
															<Typography component="span" variant="body2">
																{user.firstName} {user.lastName}
															</Typography>
															{user.roles.length > 0 && (
																<Box
																	mt={0.5}
																	display="flex"
																	gap={0.5}
																	flexWrap="wrap"
																>
																	{user.roles.map((role) => (
																		<Chip
																			key={role}
																			label={role}
																			size="small"
																		/>
																	))}
																</Box>
															)}
														</>
													}
												/>
											</ListItem>
										))}
									</List>
								)}
							</>
						)}

						{type === "configurations" && (
							<>
								{configurations.length === 0 ? (
									<Typography color="text.secondary" textAlign="center" py={3}>
										No extraction configurations found in this tenant
									</Typography>
								) : (
									<List>
										{configurations.map((config) => (
											<ListItem key={config.extractionConfigurationID} divider>
												<ListItemText primary={config.name} />
											</ListItem>
										))}
									</List>
								)}
							</>
						)}
					</>
				)}
			</DialogContent>
			<DialogActions>
				<Button onClick={onClose}>Close</Button>
			</DialogActions>
		</Dialog>
	);
};
